// src/pages/Admin/Verifications.jsx
// ✅ ALL BUGS FIXED:
//  - verifyBuyer / rejectVerification now exist in adminAPI
//  - Admin can view uploaded KYC document (download link)
//  - Default filter changed to 'all' so all verifications load
import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Typography, Box, Button, Grid, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, IconButton, TextField, InputAdornment, Avatar,
  Select, FormControl, InputLabel, MenuItem, Tooltip, LinearProgress, Tabs, Tab,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert,
} from '@mui/material';
import {
  Search, Refresh, Visibility, CheckCircle, Cancel,
  VerifiedUser, HourglassEmpty, Warning, Download, Description,
  OpenInNew,
} from '@mui/icons-material';
import { adminAPI } from '../../services/api';
import { formatDate } from '../../utils/helpers';
import toast from 'react-hot-toast';

const AdminVerifications = () => {
  const [verifications, setVerifications] = useState([]);
  const [loading,        setLoading]       = useState(true);
  const [searchTerm,     setSearchTerm]    = useState('');
  const [tabValue,       setTabValue]      = useState(0);
  const [typeFilter,     setTypeFilter]    = useState('all');
  const [selected,       setSelected]      = useState(null);
  const [viewDialog,     setViewDialog]    = useState(false);
  const [verifying,      setVerifying]     = useState(false);

  const TAB_STATUS_MAP = ['all', 'pending', 'verified', 'rejected'];

  useEffect(() => { fetchVerifications(); }, []);

  const fetchVerifications = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAllVerifications();
      const data = Array.isArray(response.data)
        ? response.data
        : (response.data?.verifications || response.data?.data || []);
      setVerifications(data);
    } catch (error) {
      console.error('Error fetching verifications:', error);
      toast.error('Failed to load verifications');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (v) => {
    setVerifying(true);
    try {
      await adminAPI.verifyBuyer({
        verification_id: v.id,
        country:         v.country,
      });
      toast.success('Buyer verified successfully');
      setViewDialog(false);
      fetchVerifications();
    } catch (err) {
      console.error('verify error', err);
      toast.error('Failed to verify buyer');
    } finally {
      setVerifying(false);
    }
  };

  const handleReject = async (v) => {
    try {
      await adminAPI.rejectVerification({
        verification_id: v.id,
        country:         v.country,
        reason:          'Documents not acceptable',
      });
      toast.success('Verification rejected');
      setViewDialog(false);
      fetchVerifications();
    } catch (err) {
      console.error('reject error', err);
      toast.error('Failed to reject verification');
    }
  };

  const openDocument = async (v, fieldName = null, docPath = null) => {
    const hasAnyDoc = (v.all_documents && v.all_documents.length > 0) || v.document_path;
    if (!hasAnyDoc) {
      toast.error('No document uploaded by this buyer');
      return;
    }

    try {
      // If we have the direct path, use the static nginx URL directly
      // This avoids blob:// issues Chrome has with PDFs
      const BACKEND = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, '');

      let targetPath = docPath;

      // If no direct path given, figure it out from field name or use document_path
      if (!targetPath) {
        if (fieldName && v.all_documents) {
          const found = v.all_documents.find(d => d.field === fieldName);
          if (found) targetPath = found.path;
        }
        if (!targetPath) targetPath = v.document_path;
      }

      if (targetPath) {
        // Serve directly via nginx static route — works for PDF and images
        const staticUrl = `${BACKEND}/static/uploads/documents/${targetPath}`;
        window.open(staticUrl, '_blank');
        return;
      }

      // Fallback: fetch via API with auth token (for edge cases)
      const token = localStorage.getItem('token');
      const apiBase = process.env.REACT_APP_API_URL || `${BACKEND}/api`;
      const url = fieldName
        ? `${apiBase}/admin/verifications/${v.id}/document?field=${fieldName}`
        : `${apiBase}/admin/verifications/${v.id}/document`;

      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { toast.error('Document not found on server'); return; }

      const contentType = res.headers.get('content-type') || 'application/octet-stream';
      const blob = new Blob([await res.arrayBuffer()], { type: contentType });
      const blobUrl = URL.createObjectURL(blob);

      // For PDFs, force download (avoids Chrome blob PDF issues); for images, open inline
      if (contentType.includes('pdf')) {
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `kyc_document_${v.id}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
      } else {
        window.open(blobUrl, '_blank');
      }
    } catch (err) {
      console.error('openDocument error', err);
      toast.error('Failed to open document');
    }
  };

  const stats = {
    total:    verifications.length,
    pending:  verifications.filter(v => !v.verified && !v.rejected).length,
    verified: verifications.filter(v => v.verified).length,
    rejected: verifications.filter(v => v.rejected).length,
  };

  const statusTab = TAB_STATUS_MAP[tabValue];
  const filteredVerifications = verifications.filter(v => {
    const matchesStatus =
      statusTab === 'all'      ? true :
      statusTab === 'pending'  ? (!v.verified && !v.rejected) :
      statusTab === 'verified' ? v.verified :
      statusTab === 'rejected' ? v.rejected : true;
    const matchesType =
      typeFilter === 'all' || (v.country || '').toLowerCase() === typeFilter.toLowerCase();
    const q = searchTerm.toLowerCase();
    const matchesSearch =
      !q ||
      (v.buyerName  || v.user?.company_name || '').toLowerCase().includes(q) ||
      (v.buyerEmail || v.user?.email        || '').toLowerCase().includes(q) ||
      (v.gst_number || v.tax_registration_number || '').toLowerCase().includes(q);
    return matchesStatus && matchesType && matchesSearch;
  });

  if (loading) return (
    <Container sx={{ py: 4 }}>
      <LinearProgress sx={{ mb: 2 }} />
      <Typography>Loading verifications...</Typography>
    </Container>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Buyer Verifications</Typography>
          <Typography variant="body1" color="text.secondary">
            Review KYC documents and verify buyer identities
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Refresh />} onClick={fetchVerifications}>Refresh</Button>
      </Box>

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          { label: 'Total',    value: stats.total,    color: '#2196f3', icon: <VerifiedUser /> },
          { label: 'Pending',  value: stats.pending,  color: '#ff9800', icon: <HourglassEmpty />, textColor: 'warning.main' },
          { label: 'Verified', value: stats.verified, color: '#4caf50', icon: <CheckCircle />,    textColor: 'success.main' },
          { label: 'Rejected', value: stats.rejected, color: '#f44336', icon: <Warning />,        textColor: 'error.main' },
        ].map((s, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">{s.label}</Typography>
                    <Typography variant="h4" fontWeight="bold" color={s.textColor || 'text.primary'}>
                      {s.value}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: `${s.color}20`, color: s.color }}>{s.icon}</Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} variant="scrollable">
          {['All', 'Pending Review', 'Verified', 'Rejected'].map((label, i) => (
            <Tab key={i} label={label} />
          ))}
        </Tabs>
      </Paper>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField fullWidth size="small"
              placeholder="Search by name, email, GST number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Buyer Country</InputLabel>
              <Select label="Buyer Country" value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}>
                <MenuItem value="all">All Countries</MenuItem>
                <MenuItem value="india">India</MenuItem>
                <MenuItem value="uae">UAE</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.50' }}>
              <TableRow>
                {['Buyer', 'Country', 'KYC Details', 'Document', 'Status', 'Submitted', 'Actions'].map(h => (
                  <TableCell key={h}><Typography variant="subtitle2" fontWeight="bold">{h}</Typography></TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredVerifications.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <VerifiedUser sx={{ fontSize: 60, color: 'text.secondary', display: 'block', mx: 'auto', mb: 1 }} />
                    <Typography color="text.secondary">
                      {verifications.length === 0
                        ? 'No verification requests found'
                        : 'No verifications match the filter'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredVerifications.map((v) => (
                <TableRow key={v.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 36, height: 36, bgcolor: v.verified ? 'success.light' : 'warning.light' }}>
                        {(v.buyerName || v.user?.company_name || 'B').charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2">
                          {v.buyerName || v.user?.company_name || v.user?.email || '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {v.buyerEmail || v.user?.email || ''}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={v.country || 'Unknown'} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" component="div" color="text.secondary">
                      {(v.gst_number || v.tax_registration_number) &&
                        <span><b>GST:</b> {v.gst_number || v.tax_registration_number}<br /></span>}
                      {(v.pan_number || v.fruit_license_number) &&
                        <span><b>PAN:</b> {v.pan_number || v.fruit_license_number}<br /></span>}
                      {(v.iec_number || v.import_export_license) &&
                        <span><b>IEC:</b> {v.iec_number || v.import_export_license}</span>}
                      {v.trade_license &&
                        <span><b>Trade:</b> {v.trade_license}<br /></span>}
                      {v.import_permit &&
                        <span><b>Import:</b> {v.import_permit}<br /></span>}
                      {v.company_registration &&
                        <span><b>Co.Reg:</b> {v.company_registration}</span>}
                      {!v.gst_number && !v.tax_registration_number &&
                       !v.pan_number && !v.fruit_license_number &&
                       !v.iec_number && !v.import_export_license &&
                       !v.trade_license && !v.import_permit && !v.company_registration && '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {v.all_documents && v.all_documents.length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {v.all_documents.map((doc, idx) => (
                          <Tooltip key={idx} title={`Open ${doc.label || doc.field}`}>
                            <Button size="small" variant="outlined" startIcon={<Description />}
                              onClick={() => openDocument(v, doc.field, doc.path)}
                              sx={{ justifyContent: 'flex-start', fontSize: '0.7rem', textTransform: 'none' }}>
                              {doc.label || doc.field}
                            </Button>
                          </Tooltip>
                        ))}
                      </Box>
                    ) : v.document_path ? (
                      <Tooltip title="View uploaded KYC document">
                        <Button size="small" variant="outlined" startIcon={<Description />}
                          onClick={() => openDocument(v, null, v.document_path)}>
                          View Doc
                        </Button>
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.disabled">No file</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {/* If verified_at is set but not currently verified → re-submitted after prior approval */}
                    {(() => {
                      const resubmitted = !v.verified && !v.rejected && v.verified_at;
                      return (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Chip
                            label={v.verified ? 'Verified' : v.rejected ? 'Rejected' : 'Pending'}
                            size="small"
                            color={v.verified ? 'success' : v.rejected ? 'error' : 'warning'}
                          />
                          {resubmitted && (
                            <Chip label="Re-submitted" size="small" color="info" variant="outlined"
                              sx={{ fontSize: '0.65rem' }} />
                          )}
                        </Box>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatDate(v.created_at || v.submitted_at)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="View All Details">
                        <IconButton size="small" color="primary"
                          onClick={() => { setSelected(v); setViewDialog(true); }}>
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      {!v.verified && !v.rejected && (
                        <>
                          <Tooltip title="Verify Buyer">
                            <IconButton size="small" color="success" onClick={() => handleVerify(v)}>
                              <CheckCircle />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton size="small" color="error" onClick={() => handleReject(v)}>
                              <Cancel />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            Showing {filteredVerifications.length} of {verifications.length} verifications
          </Typography>
        </Box>
      </Paper>

      {/* View Dialog */}
      <Dialog open={viewDialog} onClose={() => setViewDialog(false)} maxWidth="sm" fullWidth>
        {selected && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">KYC Verification Details</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  label={selected.verified ? 'Verified' : selected.rejected ? 'Rejected' : 'Pending'}
                  color={selected.verified ? 'success' : selected.rejected ? 'error' : 'warning'}
                  size="small"
                />
                {!selected.verified && !selected.rejected && selected.verified_at && (
                  <Chip label="Re-submitted ↺" size="small" color="info" variant="outlined" />
                )}
              </Box>
              </Box>
            </DialogTitle>
            <DialogContent>
              {!selected.verified && !selected.rejected && selected.verified_at && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight="medium">
                    🔄 Re-submitted after previous approval
                  </Typography>
                  <Typography variant="body2">
                    Previously verified on: <strong>{formatDate(selected.verified_at)}</strong>.
                    Buyer has updated their documents and needs re-verification.
                  </Typography>
                </Alert>
              )}
              {!selected.verified && !selected.rejected && !selected.verified_at && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Please review the KYC details and uploaded document before verifying.
                </Alert>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                {[
                  ['Buyer Name',    selected.buyerName  || selected.user?.company_name],
                  ['Email',         selected.buyerEmail || selected.user?.email],
                  ['Phone',         selected.user?.phone],
                  ['Country',       selected.country],
                  ['Submitted On',  formatDate(selected.created_at)],
                  // India KYC
                  ['GST Number',    selected.gst_number || selected.tax_registration_number],
                  ['PAN Number',    selected.pan_number || selected.fruit_license_number],
                  ['IEC Code',      selected.iec_number || selected.import_export_license],
                  ['Biz Reg. Cert', selected.business_registration_certificate],
                  // UAE
                  ['Trade License', selected.trade_license],
                  ['Import Permit', selected.import_permit],
                  ['Company Reg.',  selected.company_registration],
                ].map(([label, value]) => value ? (
                  <Box key={label} sx={{ display: 'flex', gap: 2, borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}>
                    <Typography variant="body2" fontWeight="bold" sx={{ width: 130, flexShrink: 0, color: 'text.secondary' }}>
                      {label}:
                    </Typography>
                    <Typography variant="body2">{value}</Typography>
                  </Box>
                ) : null)}

                {/* Document section */}
                <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}>
                  <Typography variant="body2" fontWeight="bold" color="text.secondary" sx={{ mb: 1 }}>
                    Uploaded KYC Documents:
                  </Typography>
                  {selected.all_documents && selected.all_documents.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {selected.all_documents.map((doc, idx) => (
                        <Button
                          key={idx}
                          variant="outlined"
                          size="small"
                          startIcon={<OpenInNew />}
                          onClick={() => openDocument(selected, doc.field, doc.path)}
                          sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                        >
                          {doc.label || doc.field}
                          {doc.path && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              ({doc.path.split('_').slice(1).join('_').substring(0, 20)})
                            </Typography>
                          )}
                        </Button>
                      ))}
                    </Box>
                  ) : selected.document_path ? (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<OpenInNew />}
                      onClick={() => openDocument(selected, null, selected.document_path)}
                      sx={{ mt: 0.5 }}
                    >
                      Open KYC Document
                    </Button>
                  ) : (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      No documents uploaded yet. Buyer may have only submitted text fields.
                    </Alert>
                  )}
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setViewDialog(false)}>Close</Button>
              {!selected.verified && !selected.rejected && (
                <>
                  <Button variant="outlined" color="error"
                    onClick={() => handleReject(selected)}>
                    Reject
                  </Button>
                  <Button variant="contained" color="success"
                    disabled={verifying}
                    onClick={() => handleVerify(selected)}>
                    {verifying ? 'Verifying...' : 'Verify Buyer'}
                  </Button>
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default AdminVerifications;
